import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FaArrowLeft, FaFileInvoice } from 'react-icons/fa';
import { getDocumentos, getDocumento, anular, corregir, nuevoIdPeticion } from '../../services/facturadorService';
import {
  NOMBRES_TIPO, ESTADOS, FORMAS_PAGO, pesos, fechaCorta, hoyChile, mensajeError, EstadoBadge, nombreDocumento, FiltroPeriodo,
} from './comun';
import '../../styles/pages/Facturador.css';

const POR_PAGINA = 50;
const CODIGOS_REFERENCIA = { 1: 'anula', 2: 'corrige texto', 3: 'corrige monto' };

/** AAAA-MM → último día del mes, AAAA-MM-DD */
const finDeMes = (mes) => {
  const [anio, numero] = mes.split('-').map(Number);
  return new Date(Date.UTC(anio, numero, 0)).toISOString().slice(0, 10);
};

const aceptado = (documento) => ['aceptado', 'aceptado_reparos'].includes(documento.estado);
const puedeAnular = (documento) => aceptado(documento) && [33, 34, 56, 61].includes(documento.tipo) && Number(documento.monto_total) > 0;
const puedeCorregir = (documento) => aceptado(documento) && [33, 34].includes(documento.tipo);

/** Mensaje de una nota recién emitida, con su enlace. */
const avisarNota = (nota) =>
  Swal.fire({
    icon: nota.estado === 'rechazado' ? 'error' : 'success',
    title: `${nombreDocumento(nota)} emitida`,
    html: `${ESTADOS[nota.estado]?.texto || nota.estado}. ${nota.pdf_url ? `<a href="${nota.pdf_url}" target="_blank" rel="noreferrer">Ver PDF</a>` : ''}`,
  });

// ─── Modal anular ─────────────────────────────────────────────────────────────

function AnularModal({ id_company, documento, onListo, onCerrar }) {
  const [razon, setRazon] = useState('');
  const [enviando, setEnviando] = useState(false);
  const nota = documento.tipo === 61 ? 'una nota de débito' : 'una nota de crédito';

  const confirmar = async () => {
    setEnviando(true);
    try {
      const resultado = await anular(id_company, documento.id, razon.trim() || undefined, nuevoIdPeticion());
      onListo(resultado);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se anuló', text: mensajeError(err, 'No se pudo anular el documento') });
      setEnviando(false);
    }
  };

  return (
    <div className="fe-modal-overlay" onClick={onCerrar}>
      <div className="fe-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="fe-modal-title">Anular {nombreDocumento(documento)}</h3>
        <p>Se emite {nota} por el total ({pesos(documento.monto_total)}) que anula el documento completo. Cuenta en tu uso del mes.</p>
        <div className="fe-form-group">
          <label>Motivo <span className="fe-muted">(opcional)</span></label>
          <input value={razon} maxLength={90} onChange={(e) => setRazon(e.target.value)} placeholder="Ej.: receptor equivocado" />
        </div>
        <div className="fe-form-actions">
          <button className="fe-btn fe-btn--danger" disabled={enviando} onClick={confirmar}>
            {enviando ? 'Anulando...' : 'Anular'}
          </button>
          <button className="fe-btn fe-btn--secondary" onClick={onCerrar}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal corregir ───────────────────────────────────────────────────────────

function CorregirModal({ id_company, documento, onListo, onCerrar }) {
  const unaLinea = (documento.datos?.lineas?.length ?? 0) === 1;
  const exento = documento.tipo === 34;
  const [clase, setClase] = useState('monto');
  const [modoMonto, setModoMonto] = useState(unaLinea ? 'correcto' : 'diferencia');
  const [dondeDice, setDondeDice] = useState('');
  const [debeDecir, setDebeDecir] = useState('');
  const [montoCorrecto, setMontoCorrecto] = useState('');
  const [sentido, setSentido] = useState('rebaja');
  const [glosa, setGlosa] = useState('');
  const [diferencia, setDiferencia] = useState('');
  const [conIva, setConIva] = useState(false);
  const [razon, setRazon] = useState('');
  const [enviando, setEnviando] = useState(false);

  const correccion = () => {
    if (clase === 'texto') return { tipo: 'texto', dondeDice, debeDecir };
    if (modoMonto === 'correcto') return { tipo: 'monto', montoCorrecto: Number(montoCorrecto), montosConIva: !exento && conIva };
    return {
      tipo: 'monto',
      sentido,
      montosConIva: !exento && conIva,
      lineas: [{ glosa: glosa.trim() || 'Corrige monto', precioUnitario: Number(diferencia) }],
    };
  };

  const completa = () => {
    if (clase === 'texto') return dondeDice.trim() && debeDecir.trim();
    if (modoMonto === 'correcto') return Number(montoCorrecto) > 0;
    return Number(diferencia) > 0;
  };

  const confirmar = async () => {
    setEnviando(true);
    try {
      const resultado = await corregir(id_company, documento.id, correccion(), razon.trim() || undefined, nuevoIdPeticion());
      onListo(resultado);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se corrigió', text: mensajeError(err, 'No se pudo corregir el documento') });
      setEnviando(false);
    }
  };

  return (
    <div className="fe-modal-overlay" onClick={onCerrar}>
      <div className="fe-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="fe-modal-title">Corregir {nombreDocumento(documento)}</h3>
        <div className="fe-opciones">
          <button type="button" className={`fe-tipo-btn ${clase === 'monto' ? 'fe-tipo-btn--activo' : ''}`} onClick={() => setClase('monto')}>
            El monto
          </button>
          <button type="button" className={`fe-tipo-btn ${clase === 'texto' ? 'fe-tipo-btn--activo' : ''}`} onClick={() => setClase('texto')}>
            Un texto (razón social, giro, glosa…)
          </button>
        </div>

        {clase === 'texto' ? (
          <>
            <p className="fe-hint">Se emite una nota de crédito sin montos que dice dónde estaba el error y cómo debe decir.</p>
            <div className="fe-form-group">
              <label>Dónde dice</label>
              <input value={dondeDice} onChange={(e) => setDondeDice(e.target.value)} placeholder="Ej.: Giro: Construcción" />
            </div>
            <div className="fe-form-group" style={{ marginTop: 10 }}>
              <label>Debe decir</label>
              <input value={debeDecir} onChange={(e) => setDebeDecir(e.target.value)} placeholder="Ej.: Giro: Obras civiles" />
            </div>
          </>
        ) : (
          <>
            {unaLinea && (
              <div className="fe-opciones">
                <label className="fe-checkbox">
                  <input type="radio" checked={modoMonto === 'correcto'} onChange={() => setModoMonto('correcto')} /> Indicar el monto correcto
                </label>
                <label className="fe-checkbox">
                  <input type="radio" checked={modoMonto === 'diferencia'} onChange={() => setModoMonto('diferencia')} /> Indicar la diferencia
                </label>
              </div>
            )}
            {modoMonto === 'correcto' ? (
              <>
                <p className="fe-hint">
                  Hoy el documento es por {pesos(exento ? documento.monto_exento : documento.monto_neto)}
                  {exento ? '' : ' neto'}. Si el correcto es menor se emite una nota de crédito por la diferencia; si es mayor, una nota de débito.
                </p>
                <div className="fe-form-group">
                  <label>Monto correcto</label>
                  <input type="number" min="0" value={montoCorrecto} onChange={(e) => setMontoCorrecto(e.target.value)} />
                </div>
              </>
            ) : (
              <>
                <div className="fe-opciones">
                  <label className="fe-checkbox">
                    <input type="radio" checked={sentido === 'rebaja'} onChange={() => setSentido('rebaja')} /> Rebajar (nota de crédito)
                  </label>
                  <label className="fe-checkbox">
                    <input type="radio" checked={sentido === 'aumento'} onChange={() => setSentido('aumento')} /> Aumentar (nota de débito)
                  </label>
                </div>
                <div className="fe-form-group">
                  <label>Glosa</label>
                  <input value={glosa} onChange={(e) => setGlosa(e.target.value)} placeholder="Ej.: Descuento por pronto pago" />
                </div>
                <div className="fe-form-group" style={{ marginTop: 10 }}>
                  <label>Diferencia</label>
                  <input type="number" min="0" value={diferencia} onChange={(e) => setDiferencia(e.target.value)} />
                </div>
              </>
            )}
            {!exento && (
              <label className="fe-checkbox" style={{ marginTop: 10 }}>
                <input type="checkbox" checked={conIva} onChange={(e) => setConIva(e.target.checked)} /> El monto incluye IVA
              </label>
            )}
          </>
        )}

        <div className="fe-form-group" style={{ marginTop: 12 }}>
          <label>Motivo <span className="fe-muted">(opcional)</span></label>
          <input value={razon} maxLength={90} onChange={(e) => setRazon(e.target.value)} />
        </div>
        <div className="fe-form-actions">
          <button className="fe-btn fe-btn--success" disabled={enviando || !completa()} onClick={confirmar}>
            {enviando ? 'Emitiendo...' : 'Emitir corrección'}
          </button>
          <button className="fe-btn fe-btn--secondary" onClick={onCerrar}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal detalle ────────────────────────────────────────────────────────────

function DetalleModal({ documento, onVer, onAnular, onCorregir, onCerrar }) {
  const datos = documento.datos || {};
  const receptor = datos.receptor || {};
  const totales = datos.totales || {};
  return (
    <div className="fe-modal-overlay" onClick={onCerrar}>
      <div className="fe-modal fe-modal--ancho" onClick={(e) => e.stopPropagation()}>
        <h3 className="fe-modal-title">
          {nombreDocumento(documento)} <EstadoBadge estado={documento.estado} />
        </h3>
        {documento.glosa_estado && documento.estado !== 'aceptado' && (
          <div className={`fe-aviso ${documento.estado === 'rechazado' ? 'fe-aviso--rojo' : 'fe-aviso--amarillo'}`}>
            Respuesta del SII: {documento.glosa_estado}
          </div>
        )}
        <dl className="fe-datos">
          <dt>Fecha de emisión</dt><dd>{fechaCorta(documento.fecha_emision)}</dd>
          <dt>{documento.tipo === 46 ? 'Vendedor' : 'Receptor'}</dt>
          <dd>{documento.razon_social_receptor} · {documento.rut_receptor}</dd>
          {receptor.giro && (<><dt>Giro</dt><dd>{receptor.giro}</dd></>)}
          {receptor.direccion && (<><dt>Dirección</dt><dd>{receptor.direccion}{receptor.comuna ? `, ${receptor.comuna}` : ''}</dd></>)}
          {datos.formaPago && (<><dt>Forma de pago</dt><dd>{FORMAS_PAGO[datos.formaPago]}</dd></>)}
          {documento.track_id && (<><dt>Envío al SII</dt><dd>Track ID {documento.track_id}</dd></>)}
          {documento.origen && (<><dt>Emitido desde</dt><dd>{documento.origen === 'renata' ? 'Renata (WhatsApp)' : 'Panel'}</dd></>)}
        </dl>

        {datos.correccion ? (
          <div className="fe-aviso fe-aviso--azul">{datos.correccion}</div>
        ) : (
          (datos.lineas || []).length > 0 && (
            <table className="fe-table" style={{ marginBottom: 14 }}>
              <thead>
                <tr><th>Glosa</th><th className="fe-num">Cantidad</th><th className="fe-num">Precio</th><th className="fe-num">Monto</th></tr>
              </thead>
              <tbody>
                {datos.lineas.map((l, i) => (
                  <tr key={i}>
                    <td>
                      {l.nombre}
                      {l.descripcion && <div className="fe-muted">{l.descripcion}</div>}
                    </td>
                    <td className="fe-num">{Number(l.cantidad).toLocaleString('es-CL')}</td>
                    <td className="fe-num">{Number(l.precioUnitario).toLocaleString('es-CL', { maximumFractionDigits: 6 })}</td>
                    <td className="fe-num">{pesos(l.monto)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        <div className="fe-totales" style={{ marginBottom: 16 }}>
          {totales.neto > 0 && <div><span>Neto</span><strong>{pesos(totales.neto)}</strong></div>}
          {totales.exento > 0 && <div><span>Exento</span><strong>{pesos(totales.exento)}</strong></div>}
          {totales.iva > 0 && <div><span>IVA 19 %</span><strong>{pesos(totales.iva)}</strong></div>}
          {totales.retenido > 0 && <div><span>IVA retenido</span><strong>−{pesos(totales.retenido)}</strong></div>}
          <div className="fe-total-final"><span>Total</span><strong>{pesos(documento.monto_total)}</strong></div>
        </div>

        {documento.original && (
          <p>
            Referencia: <button className="fe-btn fe-btn--link" onClick={() => onVer(documento.original.id)}>{nombreDocumento(documento.original)}</button>
            {documento.codigo_referencia ? ` (${CODIGOS_REFERENCIA[documento.codigo_referencia]})` : ''}
          </p>
        )}
        {documento.notas?.length > 0 && (
          <>
            <h4 className="fe-panel-title" style={{ fontSize: '0.95rem' }}>Notas de este documento</h4>
            <ul className="fe-checklist" style={{ marginBottom: 16 }}>
              {documento.notas.map((nota) => (
                <li key={nota.id}>
                  <button className="fe-btn fe-btn--link" onClick={() => onVer(nota.id)}>{nombreDocumento(nota)}</button>
                  <span>{pesos(nota.monto_total)}</span>
                  <EstadoBadge estado={nota.estado} />
                </li>
              ))}
            </ul>
          </>
        )}

        <div className="fe-form-actions">
          {documento.pdf_url && <a className="fe-btn fe-btn--primary" href={documento.pdf_url} target="_blank" rel="noreferrer">PDF</a>}
          {documento.xml_url && <a className="fe-btn fe-btn--secondary" href={documento.xml_url} target="_blank" rel="noreferrer">XML</a>}
          {puedeCorregir(documento) && <button className="fe-btn fe-btn--success" onClick={onCorregir}>Corregir</button>}
          {puedeAnular(documento) && <button className="fe-btn fe-btn--danger" onClick={onAnular}>Anular</button>}
          <button className="fe-btn fe-btn--secondary" onClick={onCerrar}>Cerrar</button>
        </div>
        {!aceptado(documento) && ['por_enviar', 'enviado'].includes(documento.estado) && (
          <p className="fe-hint">Se puede anular o corregir cuando el SII lo acepte.</p>
        )}
      </div>
    </div>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────

const DocumentosPage = ({ id_company }) => {
  const navigate = useNavigate();
  const hoy = hoyChile();
  const [anio, setAnio] = useState(hoy.slice(0, 4));
  const [mes, setMes] = useState(hoy.slice(5, 7)); // '' = todo el año
  const [tipo, setTipo] = useState('');
  const [estado, setEstado] = useState('');
  const [folio, setFolio] = useState('');
  const [documentos, setDocumentos] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [hayMas, setHayMas] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [detalle, setDetalle] = useState(null);
  const [accion, setAccion] = useState(null);

  const filtros = useMemo(() => {
    const f = {};
    if (folio) f.folio = folio;
    else if (mes) {
      f.desde = `${anio}-${mes}-01`;
      f.hasta = finDeMes(`${anio}-${mes}`);
    } else {
      f.desde = `${anio}-01-01`;
      f.hasta = `${anio}-12-31`;
    }
    if (tipo) f.tipo = tipo;
    if (estado) f.estado = estado;
    return f;
  }, [anio, mes, tipo, estado, folio]);

  const cargar = useCallback(async (paginaPedida) => {
    setCargando(true);
    try {
      const lista = await getDocumentos(id_company, { ...filtros, pagina: paginaPedida, limite: POR_PAGINA });
      setDocumentos((actuales) => (paginaPedida === 1 ? lista : [...actuales, ...lista]));
      setHayMas(lista.length === POR_PAGINA);
      setPagina(paginaPedida);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudieron cargar los documentos') });
    } finally {
      setCargando(false);
    }
  }, [id_company, filtros]);

  useEffect(() => { cargar(1); }, [cargar]);

  const verDetalle = async (id) => {
    try {
      setDetalle(await getDocumento(id_company, id));
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudo abrir el documento') });
    }
  };

  const notaEmitida = (nota) => {
    setAccion(null);
    setDetalle(null);
    avisarNota(nota);
    cargar(1);
  };

  return (
    <div className="fe-page">
      <header className="fe-header">
        <button className="fe-back-btn" onClick={() => navigate('/facturador')}>
          <FaArrowLeft /> Volver
        </button>
        <div className="fe-header-titulo">
          <div>
            <h1 className="fe-title">Documentos</h1>
            <p className="fe-sub">Lo emitido desde el panel y desde Renata, con su estado en el SII</p>
          </div>
        </div>
      </header>

      <div className="fe-filtros">
        <FiltroPeriodo
          anio={anio}
          mes={mes}
          onCambio={(nuevoAnio, nuevoMes) => { setAnio(nuevoAnio); setMes(nuevoMes); }}
          conTodoElAnio
          disabled={!!folio}
        />
        <div className="fe-form-group">
          <label>Tipo</label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="">Todos</option>
            {Object.entries(NOMBRES_TIPO).map(([codigo, nombre]) => <option key={codigo} value={codigo}>{nombre}</option>)}
          </select>
        </div>
        <div className="fe-form-group">
          <label>Estado</label>
          <select value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="">Todos</option>
            {Object.entries(ESTADOS).map(([clave, info]) => <option key={clave} value={clave}>{info.texto}</option>)}
          </select>
        </div>
        <div className="fe-form-group">
          <label>Folio</label>
          <input type="number" min="1" value={folio} onChange={(e) => setFolio(e.target.value)} placeholder="Cualquier mes" />
        </div>
      </div>

      <div className="fe-table-container">
        {cargando && documentos.length === 0 ? (
          <p className="fe-loading">Cargando documentos...</p>
        ) : documentos.length === 0 ? (
          <div className="fe-empty">
            <FaFileInvoice style={{ fontSize: '2.2rem', opacity: 0.4 }} />
            <p>No hay documentos con estos filtros.</p>
          </div>
        ) : (
          <table className="fe-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Documento</th>
                <th>Receptor</th>
                <th className="fe-num">Total</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {documentos.map((d) => (
                <tr key={d.id}>
                  <td>{fechaCorta(d.fecha_emision)}</td>
                  <td>
                    {nombreDocumento(d)}
                    {d.origen === 'renata' && <div className="fe-muted">Pedido a Renata</div>}
                  </td>
                  <td>
                    {d.razon_social_receptor}
                    <div className="fe-muted">{d.rut_receptor}</div>
                  </td>
                  <td className="fe-num">{pesos(d.monto_total)}</td>
                  <td><EstadoBadge estado={d.estado} /></td>
                  <td>
                    <div className="fe-acciones">
                      <button className="fe-btn fe-btn--link" onClick={() => verDetalle(d.id)}>Ver</button>
                      {d.pdf_url && <a href={d.pdf_url} target="_blank" rel="noreferrer">PDF</a>}
                      {d.xml_url && <a href={d.xml_url} target="_blank" rel="noreferrer">XML</a>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {hayMas && (
        <div className="fe-form-actions">
          <button className="fe-btn fe-btn--secondary" disabled={cargando} onClick={() => cargar(pagina + 1)}>
            {cargando ? 'Cargando...' : 'Cargar más'}
          </button>
        </div>
      )}

      {detalle && !accion && (
        <DetalleModal
          documento={detalle}
          onVer={verDetalle}
          onAnular={() => setAccion('anular')}
          onCorregir={() => setAccion('corregir')}
          onCerrar={() => setDetalle(null)}
        />
      )}
      {detalle && accion === 'anular' && (
        <AnularModal id_company={id_company} documento={detalle} onListo={notaEmitida} onCerrar={() => setAccion(null)} />
      )}
      {detalle && accion === 'corregir' && (
        <CorregirModal id_company={id_company} documento={detalle} onListo={notaEmitida} onCerrar={() => setAccion(null)} />
      )}
    </div>
  );
};

export default DocumentosPage;
